import { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Spin } from "antd";
import { ApiClientContext } from "../../../App";
import { SummaryReportDTO, SummaryReportPayload } from "../types";
import {
  getColor,
  getResolution,
  buildBarData,
  toApiStartDate,
  toApiEndDate,
} from "../utils";
import StatsBar, { GroupBy } from "./StatsBar";
import ActivityChart from "./ActivityChart";
import BreakdownTable from "./BreakdownTable";
import DonutChart from "./DonutChart";

const Summary = () => {
  const location = useLocation();
  const { apiClient } = useContext(ApiClientContext);
  const [data, setData] = useState<SummaryReportDTO | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [groupBy, setGroupBy] = useState<GroupBy>("project");

  const params = new URLSearchParams(location.search);
  const startDate = params.get("startDate") ?? "";
  const endDate = params.get("endDate") ?? "";
  const projectIds = params.get("projectIds") ?? "";
  const employeeIds = params.get("employeeIds") ?? "";

  useEffect(() => {
    if (!startDate || !endDate) return;

    const payload: SummaryReportPayload = {
      startDate: toApiStartDate(startDate),
      endDate: toApiEndDate(endDate),
      projectIds: projectIds ? projectIds.split(",") : [],
      employeeIds: employeeIds ? employeeIds.split(",") : [],
    };

    const fetchReport = async () => {
      try {
        setIsLoading(true);
        const { data: responseData } = await apiClient.post(
          "/api/time-tracking/reports/summary",
          payload,
        );
        setData(responseData);
      } catch (err) {
        console.warn(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchReport();
  }, [startDate, endDate, projectIds, employeeIds]);

  const breakdownKeys =
    groupBy === "project"
      ? (data?.projectBreakdown ?? []).map((item) => ({
          key: item.projectName,
          color: getColor(item.color, item.projectName),
          minutes: item.durationMinutes,
          totalEntries: 0,
        }))
      : (data?.userBreakdown ?? []).map((item) => ({
          key: item.fullName,
          color: getColor(undefined, item.fullName),
          minutes: item.durationMinutes,
          totalEntries: 0,
        }));

  const resolution = getResolution(startDate, endDate);

  const barData = buildBarData(data?.days ?? [], resolution, groupBy);

  const donutData =
    groupBy === "project"
      ? breakdownKeys.map((item) => ({
          name: item.key,
          value: item.minutes,
          color: item.color,
        }))
      : (data?.userBreakdown ?? []).map((user) => ({
          name: user.fullName,
          value: user.durationMinutes,
          color: getColor(undefined, user.fullName),
        }));

  return (
    <Spin spinning={isLoading}>
      <StatsBar
        data={data}
        groupBy={groupBy}
        onGroupByChange={setGroupBy}
        workingDaysRo={data?.workingDaysRomania ?? 0}
        workingDaysGe={data?.workingDaysGeorgia ?? 0}
      />
      <ActivityChart
        barData={barData}
        breakdownKeys={breakdownKeys}
        resolution={resolution}
      />
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
        <BreakdownTable
          groupBy={groupBy}
          projectBreakdown={data?.projectBreakdown ?? []}
          userBreakdown={data?.userBreakdown ?? []}
        />
        <DonutChart
          data={donutData}
          totalDurationMinutes={data?.totalDurationMinutes ?? null}
        />
      </div>
    </Spin>
  );
};

export default Summary;
